import { datadogLogs } from "@datadog/browser-logs";
import type { RumEvent } from "@datadog/browser-rum";
import { datadogRum } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react";

export interface InitObservabilityOptions {
  applicationId: string;
  clientToken: string;
  version: string;
  env: string;
}

export function initObservability({
  applicationId,
  clientToken,
  version,
  env,
}: InitObservabilityOptions): void {
  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: "partners-app",
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 1,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    plugins: [reactPlugin({ router: true })],
    allowedTracingUrls: [/^https:\/\/[^/]+\.getredo\.com(?:\/|$)/],
    beforeSend: redactSensitiveQueryParams,
  });
  datadogLogs.init({
    clientToken,
    site: "datadoghq.com",
    forwardErrorsToLogs: true,
    sessionSampleRate: 100,
    service: "partners-app",
    version,
  });
}

// Defense in depth alongside `stripTokenFromUrl()`. Stripping happens
// at app boot, but RUM session replay can capture URL state asynchronously
// — this hook runs on every event, redacting `?token=` from any URL that
// slips through (including e.g. a navigation event the SDK queues before
// our strip runs).
export function redactSensitiveQueryParams(event: RumEvent): boolean {
  if (event.view?.url) {
    event.view.url = redactToken(event.view.url);
  }
  if (event.type === "resource" && event.resource?.url) {
    event.resource.url = redactToken(event.resource.url);
  }
  return true;
}

// Regex used both as a fast pre-check (skip URL parsing when no token
// is present) and as a fallback for URLs that fail to parse. The
// pattern matches `?token=...` or `&token=...` up to the next `&` or
// end-of-string, preserving the leading separator so the rest of the
// query stays well-formed.
const TOKEN_PARAM_PATTERN = /([?&])token=[^&]*/gi;

function redactToken(url: string): string {
  // Hot-path short-circuit: most events don't have ?token=. Skip the
  // URL constructor entirely when the substring isn't present.
  if (!url.includes("token=")) {
    return url;
  }
  try {
    const parsed = new URL(url);
    if (parsed.searchParams.has("token")) {
      parsed.searchParams.set("token", "[REDACTED]");
    }
    return parsed.toString();
  } catch {
    // Fallback: malformed URLs still get the token stripped. Without
    // this, a non-parseable URL containing `?token=...` would leak the
    // token unchanged (the parser short-circuits to `return url`).
    return url.replace(TOKEN_PARAM_PATTERN, "$1token=[REDACTED]");
  }
}
